import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

class PdpPromoWidget extends Component {
  #resetTimeout;

  connectedCallback() {
    super.connectedCallback();
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener('click', this.#handleCopyClick);

    if (this.dataset.currentVariantId) {
      this.#setActiveVariant(this.dataset.currentVariantId);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const target = this.closest('.shopify-section, dialog');

    target?.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.removeEventListener('click', this.#handleCopyClick);

    if (this.#resetTimeout) {
      window.clearTimeout(this.#resetTimeout);
    }
  }

  #handleVariantUpdate = (event) => {
    const { productId } = event.detail.data;

    if (productId && this.dataset.productId && productId !== this.dataset.productId) return;

    const variantId = event.detail.resource?.id;

    if (!variantId) return;

    this.dataset.currentVariantId = String(variantId);
    this.#setActiveVariant(String(variantId));
  };

  #handleCopyClick = async (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-copy-button]') : null;

    if (!(button instanceof HTMLButtonElement)) return;

    const item = button.closest('[data-promo-variant]');

    if (!(item instanceof HTMLElement)) return;

    const discountCode = item.dataset.discountCode;

    if (!discountCode) return;

    this.#showCopiedState(item);

    try {
      await this.#copyText(discountCode);
    } catch (error) {
      console.error('Failed to copy promo code', error);
    }
  };

  #setActiveVariant(variantId) {
    const promoItems = this.querySelectorAll('[data-promo-variant]');
    let hasActivePromo = false;

    for (const item of promoItems) {
      if (!(item instanceof HTMLElement)) continue;

      const isActive = item.dataset.variantId === variantId;

      item.hidden = !isActive;
      item.classList.toggle('is-active', isActive);

      if (!isActive) {
        this.#resetCopiedState(item);
      } else {
        hasActivePromo = true;
      }
    }

    this.hidden = !hasActivePromo;
  }

  async #copyText(text) {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const input = document.createElement('textarea');
    input.value = text;
    input.setAttribute('readonly', '');
    input.style.position = 'fixed';
    input.style.opacity = '0';
    input.style.pointerEvents = 'none';

    document.body.append(input);
    input.select();
    input.setSelectionRange(0, input.value.length);

    const copied = document.execCommand('copy');
    input.remove();

    if (!copied) {
      throw new Error('Clipboard API unavailable');
    }
  }

  #showCopiedState(item) {
    const activeItem = this.querySelector('[data-promo-variant].is-active');

    if (activeItem instanceof HTMLElement && activeItem !== item) {
      this.#resetCopiedState(activeItem);
    }

    item.setAttribute('data-copied', 'true');
    const button = item.querySelector('[data-copy-button]');
    if (button instanceof HTMLButtonElement) {
      button.setAttribute('data-copied', 'true');
    }

    if (this.refs.liveRegion instanceof HTMLElement) {
      this.refs.liveRegion.textContent = this.refs.liveRegion.dataset.successMessage || '';
    }

    if (this.#resetTimeout) {
      window.clearTimeout(this.#resetTimeout);
    }

    this.#resetTimeout = window.setTimeout(() => {
      this.#resetCopiedState(item);

      if (this.refs.liveRegion instanceof HTMLElement) {
        this.refs.liveRegion.textContent = '';
      }
    }, 2200);
  }

  #resetCopiedState(item) {
    item.removeAttribute('data-copied');

    const button = item.querySelector('[data-copy-button]');
    if (button instanceof HTMLButtonElement) {
      button.removeAttribute('data-copied');
    }
  }
}

if (!customElements.get('pdp-promo-widget')) {
  customElements.define('pdp-promo-widget', PdpPromoWidget);
}
